// ===========================================
// components/LoadMoreTrigger.js
// ===========================================
import React, { useEffect, useRef } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import LoadingSpinner from './LoadingSpinner';

const LoadMoreTrigger = ({ onLoadMore, isLoading, hasMore }) => {
  const lastTriggeredRef = useRef(0);
  
  // Use intersection observer to trigger load more when element comes into view
  const { elementRef, isIntersecting } = useIntersectionObserver({
    disabled: false, // Enable intersection observer
    threshold: 0.1,
    rootMargin: '100px' // Trigger 100px before element is visible
  });

  // Trigger load more when element becomes visible
  useEffect(() => {
    if (isIntersecting && hasMore && !isLoading) {
      const now = Date.now();
      if (now - lastTriggeredRef.current > 500) { // Debounce to 500ms
        console.log('📜 Load more triggered by scroll intersection');
        lastTriggeredRef.current = now;
        onLoadMore();
      }
    }
  }, [isIntersecting, hasMore, isLoading, onLoadMore]);

  if (!hasMore) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-gray-500">🎉 You've reached the end! No more books to load.</p>
      </div>
    );
  }

  return (
    <div 
      ref={elementRef} 
      className="py-8 min-h-[120px] flex items-center justify-center border-t border-gray-200 bg-gray-50"
    >
      {isLoading ? (
        <LoadingSpinner text="Loading more books..." />
      ) : (
        <div className="text-center">
          <button
            onClick={() => {
              const now = Date.now();
              if (now - lastTriggeredRef.current > 500) { // Prevent rapid clicking
                console.log('🖱️ Load more triggered by button click');
                lastTriggeredRef.current = now;
                onLoadMore();
              }
            }}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-8 py-4 text-white transition-colors bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            Load More Books (50 per page)
          </button>
          <p className="mt-2 text-sm text-gray-500">Automatically loads when you scroll to the bottom</p>
        </div>
      )}
    </div>
  );
};

export default LoadMoreTrigger;